import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "../pages/Dashboard";
import Sparkline from "@/components/Sparkline";
import { Gauge } from "lucide-react";

// read-only KPI snapshot for the Overview dashboard.
// Hides itself when there are no active KPIs to show. NO writes.

const STATUS_TONE = {
  ok: "green", warning: "amber", critical: "red", unknown: "slate",
};
const STATUS_STROKE = {
  ok: "#10B981", warning: "#D97706", critical: "#DC2626", unknown: "#64748B",
};
const STATUS_FILL = {
  ok: "#D1FAE5", warning: "#FEF3C7", critical: "#FEE2E2", unknown: "#E2E8F0",
};

function fmt(v, unit, cc) {
  if (v == null) return "-";
  if (unit === "currency" && cc) return `${cc} ${Number(v).toFixed(2)}`;
  if (unit === "percent") return `${v}%`;
  if (unit === "hours") return `${v}h`;
  return String(v);
}

export default function KPISnapshotCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/kpi-dashboard?limit=6")
      .then((r) => setData(r.data))
      .catch(() => setData({ kpis: [], total: 0 }));
  }, []);
  if (!data) return null;
  if (!data.kpis || data.kpis.length === 0) return null;
  return (
    <Card data-testid="dash-kpi-snapshot"
      title={<span className="flex items-center gap-2">
        <Gauge className="h-4 w-4 text-[#2563EB]" />KPI snapshot
      </span>}
      action={<Link to="/app/kpis" className="text-xs text-[#2563EB] hover:underline">View all →</Link>}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {data.kpis.map((k) => (
          <Link key={k.kpi_id} to={`/app/kpis`}
            data-testid={`dash-kpi-tile-${k.kpi_id}`}
            className="block p-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-medium text-[#0B1A36] truncate">{k.name}</div>
                <div className="text-[11px] text-[#4B5773] uppercase tracking-widest">
                  {k.scope}{k.scope_name ? ` · ${k.scope_name}` : ""}
                </div>
              </div>
              <Badge tone={STATUS_TONE[k.status] || "slate"}>{k.status}</Badge>
            </div>
            <div className="mt-2 flex items-center justify-between">
              <span className="font-display text-2xl text-[#0B1A36]">
                {fmt(k.latest_value, k.unit, k.currency_code)}
              </span>
              <Sparkline points={k.sparkline} width={80} height={28}
                stroke={STATUS_STROKE[k.status] || "#2563EB"}
                fill={STATUS_FILL[k.status] || "#DBE7FE"} />
            </div>
          </Link>
        ))}
      </div>
    </Card>
  );
}

function Badge({ tone = "slate", children }) {
  const TONES = {
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-700 border-amber-200",
    red:   "bg-red-50 text-red-700 border-red-200",
    slate: "bg-slate-100 text-slate-600 border-slate-200",
  };
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border ${TONES[tone] || TONES.slate}`}>
      {children}
    </span>
  );
}
